import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isNonNullish } from 'remeda'
import { ProductDescriptionsWrapper } from '@components/ApparelProduct/ApparelProduct.styles'
import { useExchangeRates } from '@components/ExchangeRatesProvider'
import { dataTestIds } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { MinusIconSmall } from '@velasca/icons/minus-small'
import { PlusIconSmall } from '@velasca/icons/plus-small'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { ErrorMessage } from '@velasca/ui/error-message'
import { HorizontalCardDescription } from '@velasca/ui/horizontal-card-description'
import { HorizontalCardPrice } from '@velasca/ui/horizontal-card-price'
import { HorizontalCardTitle } from '@velasca/ui/horizontal-card-title'
import { SansText } from '@velasca/ui/sans-text'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import {
  Button,
  Controls,
  CurrentQuantity,
  DeleteButton,
  Details,
  EmptyImage,
  Header,
  ImageWrapper,
  ItemQuantity,
  LoadingImage,
  PriceLine,
  ProductDetails,
  ProductImage,
  ProductTitleLink,
} from './LineItem.styles'
import type { LineItemProps } from './LineItem.types'

export const LineItem = ({
  productID,
  handle,
  image,
  imgAlt,
  title,
  material,
  color,
  rawPrice,
  rawCompareAtPrice,
  productType,
  seoCaption,
  initialItemQuantity,
  size,
  className,
}: LineItemProps): JSX.Element => {
  const { changeProduct } = useCart()
  const { convert } = useExchangeRates()
  const [error, setError] = useState<string | null>(null)

  const { t } = useTranslation('global')
  const { t: cartDrawerT } = useTranslation('cartDrawer')
  const [itemQuantity, setItemQuantity] = useState(initialItemQuantity)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setItemQuantity(initialItemQuantity)
  }, [initialItemQuantity])

  const increment = useCallback(() => {
    setError(null)
    changeProduct(productID, itemQuantity + 1).catch((error) => {
      if (error.response) {
        setError(error.response.data.message)
        handleError(error)
      } else {
        setError(cartDrawerT('cart_drawer_product_error'))
        handleError(error)
      }
      setItemQuantity(initialItemQuantity)
    })
  }, [itemQuantity, initialItemQuantity, changeProduct, productID, cartDrawerT])

  const decrement = useCallback(() => {
    setError(null)

    changeProduct(productID, itemQuantity - 1).catch((error) => {
      if (error.response) {
        setError(error.response.data.message)
        handleError(error)
      } else {
        setError(cartDrawerT('cart_drawer_product_error'))
        handleError(error)
      }
      setItemQuantity(itemQuantity)
    })
  }, [cartDrawerT, itemQuantity, changeProduct, productID])

  const remove = useCallback(() => {
    setError(null)

    changeProduct(productID, 0).catch((error) => {
      if (error.response) {
        setError(error.response.data.message)
        handleError(error)
      } else {
        setError(cartDrawerT('cart_drawer_product_error'))
        handleError(error)
      }
    })
  }, [cartDrawerT, productID, changeProduct])

  useEffect(() => {
    if (handle) {
      return
    }

    setError(cartDrawerT('cart_drawer_product_error'))
    handleError('Product handle is missing')
  }, [handle, cartDrawerT])

  return (
    <div data-testid={dataTestIds.LINE_ITEM} className={className}>
      <AnimatePresence>
        {itemQuantity > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Header>
              <ConditionalLink
                condition={Boolean(handle)}
                href={`/products/${handle}`}
              >
                <ImageWrapper>
                  {image.loading && <LoadingImage />}
                  {image.error && <EmptyImage />}
                  {!image.loading && !image.error && image.data && (
                    <ProductImage
                      lazyload
                      lqip
                      showDefault
                      alt={imgAlt}
                      draggable={false}
                      transitionDuration={50}
                      sources={buildIntrinsicWidthSources({
                        src: image.data.src,
                      })}
                      imgSrc={setQueryParams(image.data.src, [
                        { name: 'width', value: '30' },
                      ])}
                    />
                  )}
                </ImageWrapper>
              </ConditionalLink>
              <Details>
                <ProductDescriptionsWrapper layout="list">
                  {productType && !seoCaption && (
                    <HorizontalCardDescription className="product-type">
                      {productType}
                    </HorizontalCardDescription>
                  )}
                  <ConditionalLink
                    CustomLink={ProductTitleLink}
                    condition={Boolean(handle)}
                    href={`/products/${handle}`}
                  >
                    <HorizontalCardTitle>{title}</HorizontalCardTitle>
                  </ConditionalLink>

                  {seoCaption && (
                    <HorizontalCardDescription className="seo-caption">
                      {seoCaption}
                    </HorizontalCardDescription>
                  )}

                  <ProductDetails>
                    {/* When the seo_caption is shown, the material line is redundant (matches the PLP card behavior) */}
                    {material && !seoCaption && (
                      <HorizontalCardDescription>
                        {material}
                      </HorizontalCardDescription>
                    )}
                    {color && (
                      <HorizontalCardDescription>
                        {color}
                      </HorizontalCardDescription>
                    )}
                    {size && (
                      <HorizontalCardDescription>{`${t(
                        'cart_item_size',
                      )} ${size}`}</HorizontalCardDescription>
                    )}
                  </ProductDetails>
                </ProductDescriptionsWrapper>

                {/* TODO: remove this dangerouslySetInnerHTML */}
                <PriceLine data-testid={dataTestIds.PRODUCT_PRICE_IN_CARD}>
                  {/* TODO: remove this dangerouslySetInnerHTML */}
                  {isNonNullish(rawCompareAtPrice) &&
                    isNonNullish(rawPrice) &&
                    rawCompareAtPrice > rawPrice && (
                      <HorizontalCardPrice
                        strikethrough
                        color="gray"
                        dangerouslySetInnerHTML={{
                          __html: convert(rawCompareAtPrice),
                        }}
                      />
                    )}
                  {isNonNullish(rawPrice) && (
                    <HorizontalCardPrice
                      fontWeight="500"
                      dangerouslySetInnerHTML={{
                        __html: convert(rawPrice),
                      }}
                    />
                  )}
                </PriceLine>
              </Details>
            </Header>

            <Controls>
              <ItemQuantity>
                <Button
                  title={t('cart_decrement')}
                  disabled={itemQuantity === 0}
                  onClick={decrement}
                >
                  <MinusIconSmall width="20" height="20" />
                </Button>
                <CurrentQuantity>
                  <SansText center>{itemQuantity}</SansText>
                </CurrentQuantity>
                <Button title={t('cart_increment')} onClick={increment}>
                  <PlusIconSmall width="20" height="20" />
                </Button>
              </ItemQuantity>
              <DeleteButton onClick={remove}>
                <SansText
                  underline
                  as="span"
                  color="primary"
                  fontWeight="400"
                  fontStyle="italic"
                  title={t('cart_delete')}
                >
                  {t('cart_delete')}
                </SansText>
              </DeleteButton>
            </Controls>

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ErrorMessage>{error}</ErrorMessage>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
